import React, { useMemo, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import type { StackedVerticalBarProps } from './BarChart.types'
import {
  BAR_CHART_DEFAULT_HEIGHT,
  BAR_CHART_DEFAULT_SEGMENTS,
  BAR_CHART_DEFAULT_TITLE,
  BAR_CHART_DEFAULT_WIDTH,
} from './BarChart.types'
import { ChartTooltip } from '../ChartTooltip'
import { getBarTooltipAnchor } from './getBarTooltipAnchor'
import './BarChart.css'

const StackedVerticalBar: React.FC<StackedVerticalBarProps> = ({
  title = BAR_CHART_DEFAULT_TITLE,
  hideTitle = false,
  width = BAR_CHART_DEFAULT_WIDTH,
  height = BAR_CHART_DEFAULT_HEIGHT,
  segments = BAR_CHART_DEFAULT_SEGMENTS,
  showTooltip = true,
  formatValue,
  darkMode = false,
  className = '',
  style,
  'aria-label': ariaLabel,
}) => {
  const columnRef = useRef<HTMLDivElement>(null)
  const [hover, setHover] = useState<{ x: number; y: number } | null>(null)
  const format = formatValue ?? ((v: number) => String(v))

  const resolvedSegments = useMemo(
    () => (segments.length > 0 ? segments : BAR_CHART_DEFAULT_SEGMENTS),
    [segments],
  )

  const classNames = [
    'ds-bar-chart',
    'ds-bar-chart--stacked',
    showTooltip ? 'ds-bar-chart--interactive' : '',
    darkMode ? 'ds-bar-chart--dark' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const rootStyle = {
    ...style,
    '--ds-bar-chart-width': `${width}px`,
    '--ds-bar-chart-height': `${height}px`,
  } as CSSProperties

  return (
    <div
      className={classNames}
      style={rootStyle}
      role="img"
      aria-label={ariaLabel ?? title}
      onMouseEnter={
        showTooltip
          ? (event) => {
              setHover(
                getBarTooltipAnchor(event.currentTarget, columnRef.current),
              )
            }
          : undefined
      }
      onMouseLeave={showTooltip ? () => setHover(null) : undefined}
    >
      <div ref={columnRef} className="ds-bar-chart__column" aria-hidden="true">
        {resolvedSegments.map((segment, index) => {
          const grow = Number.isFinite(segment.value)
            ? Math.max(0, segment.value)
            : 0
          const segmentStyle = {
            '--ds-bar-chart-segment-grow': grow,
            '--ds-bar-chart-segment-color':
              segment.color ?? 'var(--ds-blue-600)',
          } as CSSProperties

          return (
            <div
              key={`${segment.label ?? 'segment'}-${index}`}
              className="ds-bar-chart__segment"
              style={segmentStyle}
              title={segment.label}
            />
          )
        })}
      </div>
      {!hideTitle ? <p className="ds-bar-chart__title">{title}</p> : null}
      {showTooltip && hover ? (
        <ChartTooltip
          visible
          darkMode={darkMode}
          title={title}
          x={hover.x}
          y={hover.y}
          items={resolvedSegments.map((segment, index) => ({
            label: segment.label ?? `Segment ${index + 1}`,
            value: format(
              Number.isFinite(segment.value) ? Math.max(0, segment.value) : 0,
            ),
            color: segment.color ?? 'var(--ds-blue-600)',
          }))}
        />
      ) : null}
    </div>
  )
}

export default StackedVerticalBar
